/**
 * Local CMS data model — a Zendesk-shaped content tree that lives entirely in
 * the team database. Nothing here performs I/O -- these are plain shapes.
 *
 * Shape vs Zendesk:
 * - Zendesk is Category → Section → Article, sections one level deep.
 * - This model allows Section → Section nesting (`parentSectionId`), which the
 *   user explicitly asked for and Zendesk cannot represent.
 * - Localization is first-class: every content node keeps a map of
 *   locale → translation, rather than Zendesk's separate translation endpoints.
 */

export type NodeKind = "category" | "section" | "article"

/** Draft vs published, mirroring Zendesk's article states. */
export type PublishState = "draft" | "published"

/** A single locale's content for a node. */
export interface Translation {
  locale: string
  title: string
  /** HTML body. Only meaningful for articles; empty string for category/section. */
  body: string
  /** Short description, used for categories/sections and article summaries. */
  description?: string
  /** True when a human/agent has reviewed this locale. Untouched machine output stays false. */
  reviewed: boolean
  /**
   * Publish state for THIS locale. Each translation publishes independently —
   * an English article can be live while its Spanish translation is still a
   * draft awaiting review, which is how real multilingual help centers work.
   * Only meaningful on articles.
   */
  state?: PublishState
  updatedAt: number
}

/**
 * One downloadable file on an article.
 *
 * `url` is opaque: an uploaded URL (see shared/uploadImage.ts) or a legacy
 * Zendesk attachment URL, exactly as with the image fields elsewhere.
 */
export interface ArticleAttachment {
  fileName: string
  url: string
}

interface BaseNode {
  id: string
  kind: NodeKind
  /** Sort order within the parent. Lower first. */
  position: number
  /** locale code → translation. `sourceLocale` is the authoritative one. */
  translations: Record<string, Translation>
  sourceLocale: string
  createdAt: number
  updatedAt: number
}

export interface CategoryNode extends BaseNode {
  kind: "category"
}

export interface SectionNode extends BaseNode {
  kind: "section"
  categoryId: string
  /** Set when this section is nested inside another section. Null = top level of the category. */
  parentSectionId: string | null
}

export interface ArticleNode extends BaseNode {
  kind: "article"
  sectionId: string
  /**
   * @deprecated Publish state is per-locale — read `translations[locale].state`
   * via `articleState()`. Retained so knowledge bases saved before the change
   * still load, and used as the fallback for locales with no state of their own.
   */
  state: PublishState
  labels: string[]
  /**
   * Downloadable files shown beneath the article body.
   *
   * Zendesk carries these as article attachments, and the production renderer
   * lists them (signpost-monorepo lib/components/article.tsx, which filters to
   * .pdf/.doc/.docx). Without this the Smart CMS could not represent an
   * article that offers a form or leaflet to download — a real content type on
   * these sites, and silent data loss on migration.
   *
   * Not translatable: one file list per article, matching Zendesk.
   */
  attachments: ArticleAttachment[]
  /** Free-form provenance: which source file/job produced this. Helps audit agent output. */
  sourceRef?: string
}

export type AnyNode = CategoryNode | SectionNode | ArticleNode

/** The whole KB, held in memory and persisted per-row by the adapter. */
export interface KBStore {
  version: 1
  locales: string[]
  defaultLocale: string
  categories: Record<string, CategoryNode>
  sections: Record<string, SectionNode>
  articles: Record<string, ArticleNode>
}

export function emptyStore(defaultLocale = "en-US"): KBStore {
  return {
    version: 1,
    locales: [defaultLocale],
    defaultLocale,
    categories: {},
    sections: {},
    articles: {},
  }
}

/** Reads the best available translation, falling back through source → default → any. */
function pickTranslation(node: AnyNode, locale: string, defaultLocale: string): Translation | null {
  return (
    node.translations[locale] ??
    node.translations[node.sourceLocale] ??
    node.translations[defaultLocale] ??
    Object.values(node.translations)[0] ??
    null
  )
}

export function nodeTitle(node: AnyNode, locale: string, defaultLocale: string): string {
  return pickTranslation(node, locale, defaultLocale)?.title || "(untitled)"
}

/**
 * Publish state of one locale of an article.
 *
 * Falls back to the article-level `state` for locales written before publish
 * state moved onto the translation, so older stores keep working. A locale that
 * has no translation at all is reported as "draft" — nothing is published there.
 */
export function articleState(article: ArticleNode, locale: string): PublishState {
  const tr = article.translations[locale]
  if (!tr) return "draft"
  return tr.state ?? article.state ?? "draft"
}


/** Locales of this article grouped by publish state, for reporting. */
export function localesByState(article: ArticleNode): { published: string[]; draft: string[] } {
  const published: string[] = []
  const draft: string[] = []
  for (const l of Object.keys(article.translations)) {
    (articleState(article, l) === "published" ? published : draft).push(l)
  }
  return { published, draft }
}
